/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { useMenuControl } from '../../../context';
import type { NativeMenuItem, RenderableMenuItem } from '../../../types/menuV2';
import { CrossAppItem } from '../CrossAppItem/CrossAppItem';
import MenuContentItem from '../MenuItem';

/**
 * Type guard to check if an item is a native menu item
 * @param item - The item to check
 * @returns true if the item is a native menu item, false otherwise
 */
export const isNativeRenderableMenuItem = (item: RenderableMenuItem): item is NativeMenuItem => {
	return 'children' in item && !('type' in item);
};

/**
 * Props for MenuItemV2 component
 */
interface MenuItemV2Props {
	/**
	 * The menu item to render (can be native or cross-app item)
	 */
	item: RenderableMenuItem;
	/**
	 * Optional index of this item in the list
	 */
	itemIndex?: number;
	/**
	 * Optional total count of items in the list
	 */
	totalItemsCount?: number;
}

/**
 * MenuItemV2 component that handles rendering of both native and cross-app menu items.
 *
 * For native items (type='native' or no type property), renders the existing MenuItem component.
 * For cross-app items (integration, iframe, link, platform), renders the CrossAppItem component.
 *
 * @param props - Component props
 * @returns Rendered menu item or null
 */
export default function MenuItemV2({ item }: MenuItemV2Props) {
	const { crossAppCreationConfig, product } = useMenuControl();

	// Check if this is a native menu item
	if (isNativeRenderableMenuItem(item)) {
		// Render native menu item using existing MenuItem component
		return <MenuContentItem item={item} />;
	}

	// For cross-app items, check if context exists and is enabled
	if (!crossAppCreationConfig.enabled) {
		return null;
	}

	// Render cross-app item with context
	return <CrossAppItem responseItem={item} context={crossAppCreationConfig} product={product} />;
}
